import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ILocationWarningState,
  ILocationWarningType,
  IWarningPayloadData,
  IWeatherWarningsState,
  WarningLoadingState,
} from 'redux-state/weather-warnings/types.js';
import { objectAssignTypeSafety } from 'utilities/helpers/objects.js';
import { getCurrentLocationWarnings, lookupWeatherTypeKey } from 'utilities/weather-warnings/warnings.js';

export const reduxInitialStateWeatherWarnings: IWeatherWarningsState = {
  warnings: {},
};

const getNewStateForLocation = (
  existingStateForLocation: Readonly<ILocationWarningState | undefined>,
  newProps: Partial<ILocationWarningState>,
) => {
  if (existingStateForLocation) {
    return { ...existingStateForLocation, ...newProps };
  }
  return newProps;
};

const updateLocationState = (
  existingState: IWeatherWarningsState,
  { type, key }: Partial<ILocationWarningType>,
  props1: Partial<ILocationWarningState>,
  props2?: Partial<ILocationWarningState>,
) => {
  const newProps = { ...props1, ...props2 };
  const lookupKey = lookupWeatherTypeKey({ type, key });
  const warningState = getCurrentLocationWarnings({ type, key }, existingState.warnings);
  const newStateForLocationKey = getNewStateForLocation(warningState, newProps);
  const newStateForWarnings = objectAssignTypeSafety(existingState.warnings, {
    [lookupKey]: newStateForLocationKey as ILocationWarningState,
  });
  if (newStateForWarnings) {
    const newState = objectAssignTypeSafety(existingState, { warnings: newStateForWarnings });
    return newState;
  }
  return existingState;
};

export type WEATHER_WARNINGS_FETCH_TYPE<T extends string = string> = PayloadAction<
  { warningProps: ILocationWarningType; timestamp: number },
  T
>;

export const warningsSlice = createSlice({
  name: 'warnings',
  initialState: reduxInitialStateWeatherWarnings,
  reducers: {
    REDUX_WEATHER_WARNINGS_FETCH_INIT: (state, action: WEATHER_WARNINGS_FETCH_TYPE) => {
      const warningState = getCurrentLocationWarnings(action.payload.warningProps, state.warnings);
      if (warningState?.loadState === undefined) {
        return updateLocationState(state, action.payload.warningProps, {
          loadState: WarningLoadingState.loading,
        });
      }
      return state;
    },
    REDUX_WEATHER_WARNINGS_CLEAR: (state) => {
      const clearedWarningsState = objectAssignTypeSafety(state, { warnings: {} });
      return clearedWarningsState;
    },
    REDUX_WEATHER_WARNINGS_FETCH_SUCCESS: (
      state,
      action: PayloadAction<{
        locationRequest: ILocationWarningType;
        data: IWarningPayloadData;
      }>,
    ) => {
      const { data, locationRequest } = action.payload;

      // If there's no warnings then something is very wrong with the data so set an error
      if (!data.warnings) {
        console.error('WEATHER_WARNING_ERROR', state, data);
        return updateLocationState(state, locationRequest, { ...data, loadState: WarningLoadingState.failed });
      }

      // If warnings do not contain any location data, use locationRequest
      if (data.locations) {
        console.error('This warning logic should not be used');
      }

      return updateLocationState(state, locationRequest, data, { loadState: WarningLoadingState.loaded });
    },
    REDUX_WEATHER_WARNINGS_FETCH_ERROR: (
      state,
      action: PayloadAction<{
        locationRequest: ILocationWarningType;
        data: IWarningPayloadData;
      }>,
    ) => {
      console.error('WEATHER_WARNING_FETCH_ERROR', state, action.payload);
      return updateLocationState(state, action.payload.locationRequest, action.payload.data, {
        loadState: WarningLoadingState.failed,
      });
    },
    REDUX_WEATHER_WARNINGS_FETCH_RETRY: (state, _: WEATHER_WARNINGS_FETCH_TYPE) => {
      // used by saga
      return state;
    },
    REDUX_WEATHER_WARNINGS_FETCH_EXPIRED: (state, _: WEATHER_WARNINGS_FETCH_TYPE) => {
      // used by saga
      return state;
    },
  },
});

export type weatherWarningsFetchTypes =
  | typeof warningsSlice.actions.REDUX_WEATHER_WARNINGS_FETCH_INIT.type
  | typeof warningsSlice.actions.REDUX_WEATHER_WARNINGS_FETCH_RETRY.type
  | typeof warningsSlice.actions.REDUX_WEATHER_WARNINGS_FETCH_EXPIRED.type;

export const {
  REDUX_WEATHER_WARNINGS_FETCH_INIT,
  REDUX_WEATHER_WARNINGS_CLEAR,
  REDUX_WEATHER_WARNINGS_FETCH_SUCCESS,
  REDUX_WEATHER_WARNINGS_FETCH_ERROR,
  REDUX_WEATHER_WARNINGS_FETCH_RETRY,
  REDUX_WEATHER_WARNINGS_FETCH_EXPIRED,
} = warningsSlice.actions;

export const searchReducer = warningsSlice.reducer;
